// DS1.5
import React from 'react';
import c from 'classnames';
import { ThemeVariant } from '@wix/da-react-context/pkg/ThemeContext';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { Url } from '@wix/da-url';
import s from './Logo.scss';

export interface Props {
  markColor?: 'black' | 'green' | 'white';
  markColorClassname?: string; // allows to set the mark color to a CSS var
  textColor?: 'black' | 'green' | 'white';
  textColorClassname?: string; // allows to set the text color to a CSS var or hide with a media-query
  size?: 'tiny' | 'small' | 'medium';
  style?: React.CSSProperties;
  withText?: boolean;
  className?: string;
  themeVariant?: ThemeVariant;
  allowCustomization?: boolean;
}

const Logo: React.FC<Props> = ({
  className,
  style,
  markColor = 'green',
  markColorClassname,
  textColor = 'black',
  textColorClassname,
  size = 'medium',
  withText = true,
  themeVariant,
  allowCustomization = false,
}) => {
  const halloween_2025 = useFlag('halloween_2025');
  const showCustomLogo = allowCustomization && halloween_2025;

  return (
    <div
      className={c(s['root'], s[`size-${size}`], s['new-core'], className)}
      style={style}
    >
      {showCustomLogo && halloween_2025 && (
        <img
          src={Url.staticCDNLink(
            !withText
              ? '/eclipse/promos/logo-da-halloween-2025-mobile.png'
              : themeVariant === 'light'
                ? '/eclipse/promos/logo-da-halloween-2025-light-2x.png'
                : '/eclipse/promos/logo-da-halloween-2025-dark-2x.png'
          )}
          alt="DeviantArt Halloween 2025"
          style={{
            width: withText ? '104px' : '48px',
            marginLeft: withText ? '-2px' : '-16px',
            marginRight: withText ? '0px' : '-16px',
          }}
        />
      )}
      {!showCustomLogo && (
        <span
          className={c(s['mark'], markColorClassname || s[`mark-${markColor}`])}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            version="1.1"
            width="0"
            height="0"
            viewBox="0 0 100 167"
            preserveAspectRatio="xMidYMid meet"
          >
            <path d=" M100 0 L99.96 0 L99.95 0 L71.32 0 L68.26 3.04 L53.67 30.89 L49.41 33.35 L0 33.35 L0 74.97 L26.40 74.97 L29.15 77.72 L0 133.36 L0 166.5 L0 166.61 L0 166.61 L28.70 166.6 L31.77 163.55 L46.39 135.69 L50.56 133.28 L100 133.28 L100 91.68 L73.52 91.68 L70.84 89 L100 33.33 " />
          </svg>
        </span>
      )}

      {withText && !showCustomLogo && (
        <span
          className={c(s['text'], textColorClassname || s[`text-${textColor}`])}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 85 26"
          >
            <path
              d="M13.851.278c-.078.153-.427.811-.774 1.463-.347.652-.631 1.202-.631 1.221 0 .02.284.035.631.035 1.11 0 1.735.198 2.314.735.633.586.937 1.602.78 2.602-.098.624-.307 1.077-.677 1.47-.567.6-1.258.834-2.456.836h-.72V5.966c0-1.687-.023-2.654-.061-2.616-.034.033-.812 1.479-1.73 3.212L8.86 9.714v1.914l3.057-.023c3.51-.026 3.595-.037 4.787-.615.752-.364 1.276-.766 1.74-1.335 1.398-1.713 1.637-4.684.55-6.817-.796-1.562-2.498-2.613-4.522-2.795L13.994 0l-.143.278zm6.728 5.54v5.803h9.029V8.577h-5.507V7.31H29.16V4.33h-5.064l.019-.619.018-.618 2.738-.016 2.737-.017V.016H20.58V5.82zm9.94-5.69c.016.06.646 2.671 1.4 5.802l1.372 5.69 1.892.001h1.892l3.039-5.745c1.671-3.16 3.048-5.772 3.06-5.803.012-.032-.757-.057-1.798-.057h-1.818l-1.845 3.491c-1.014 1.92-1.856 3.479-1.872 3.464-.023-.023-1.027-4.127-1.558-6.368l-.139-.587h-1.827c-1.708 0-1.825.008-1.798.111zm13.37 5.69v5.803h3.522V.016H43.89V5.82zM53.923.667c-.192.358-1.494 2.82-2.893 5.47-1.4 2.65-2.625 4.97-2.724 5.153l-.18.334 1.836-.016 1.836-.017.416-.809.416-.808 2.082.015 2.08.016.192.809.191.808h1.81c1.23 0 1.81-.021 1.81-.067 0-.037-.62-2.631-1.376-5.765C58.66 2.654 58.042.074 58.042.053c0-.02-.848-.037-1.884-.037h-1.885l-.35.65zM61.5 5.82v5.802h3.52l.017-2.922.017-2.922 2.145 2.92 2.146 2.92 1.553.002 1.553.002V.016H68.93l-.017 2.743-.017 2.743-1.953-2.742L64.99.02 63.245.018 61.5.016V5.82zm11.655-4.28V3.06H76.484l.017 4.297.016 4.296.974.018c.953.017.976.014 1.066-.127.05-.08.39-.701.755-1.381l.663-1.237.032-2.917.032-2.917 1.51-.017 1.509-.017.78-1.479c.429-.813.78-1.497.78-1.52 0-.024-2.58-.043-5.731-.043h-5.732v1.522zm-17.349 4.38c.162.66.28 1.214.262 1.231-.018.018-.462.032-.986.032h-.954l.66-1.262c.363-.695.675-1.249.692-1.232.018.017.164.571.326 1.23zM6.291 14.553c-.076.15-1.446 2.745-3.044 5.77C1.65 23.348.343 25.837.343 25.856c0 .018.814.033 1.81.033l1.809-.001.436-.824.436-.823h4.144l.137.587c.076.322.165.693.2.824l.063.238H13.024l-.035-.143c-.02-.078-.65-2.683-1.4-5.787l-1.362-5.644-1.899-.016-1.898-.017-.139.27zm7.435 5.531v5.806h3.459V22.529h1.61l.827 1.68.826 1.68h3.664l-.477-.903c-.263-.497-.744-1.41-1.069-2.03l-.59-1.126.485-.448c.868-.803 1.236-1.79 1.177-3.159-.081-1.863-1.04-3.154-2.771-3.733l-.512-.172-3.314-.02-3.315-.02v5.806zm10.439-4.277v1.522h3.33v8.56H29.534l.741-1.41.741-1.411v-2.87l.001-2.87h3.042l.785-1.484c.43-.817.784-1.501.784-1.522 0-.02-2.58-.037-5.732-.037h-5.731v1.522zm-4.62 1.714c.366.204.521.507.521 1.013 0 .535-.161.83-.556 1.02-.277.133-.396.147-1.3.15l-.993.003-.018-1.108c-.01-.609-.002-1.147.017-1.196.028-.073.219-.085 1.042-.064.925.024 1.03.039 1.287.182zM8.24 21.19l.04.197h-.959c-.527 0-.959-.017-.959-.037 0-.02.097-.213.216-.428.118-.215.412-.762.652-1.215l.437-.825.267 1.055c.146.58.284 1.144.306 1.253z"
              clipRule="evenodd"
            ></path>
          </svg>
        </span>
      )}
    </div>
  );
};
Logo.displayName = 'Logo';

export default Logo;
